import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ResponsiveSankey } from '@nivo/sankey';
import { type FC, useMemo, useState } from 'react';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import buildLeadsFilterUrl from 'utils/buildLeadsFilterUrl';
import { FAIL_REASON_LABELS, type FailReason } from 'utils/failReasonLabels';

interface LeadsSankeyFilters {
  startDate: Date;
  endDate: Date;
  businessUnitId: number;
  sources?: string[];
  brands?: string[];
  campaignIds?: string[];
}

interface LeadsSankeyProps {
  totalLeads: number;
  leadsContacted: number;
  leadsFailed: number;
  leadsDiscarded: number;
  leadsPending: number;
  leadsResponded: number;
  leadsInCRM: number;
  respondedLeadsInCRM: number;
  notRespondedLeadsInCRM: number;
  failedLeadsInCRM: number;
  discardedLeadsInCRM: number;
  pendingLeadsInCRM: number;
  /** Breakdown of failed leads by `failReason`. Drives the optional Sankey sub-flow. */
  failedLeadsByReason?: Partial<Record<FailReason | 'null', number>>;
  /** Subset of `failedLeadsByReason` that ended up in the CRM. Drives the per-reason CRM split. */
  failedLeadsByReasonCRM?: Partial<Record<FailReason | 'null', number>>;
  filters?: LeadsSankeyFilters;
  onNavigateToLeads?: (url: string) => void;
}

// Prefix for fail-reason node ids so they don't collide with other Sankey node ids.
const FAIL_NODE_PREFIX = 'fail:';
// Single tone shared by all reason nodes — keep tonal hierarchy below the parent Fallidos red.
const FAIL_REASON_NODE_COLOR = '#e57373';
// Terminal for failed-reason leads that never reached the CRM (only used in breakdown mode).
const NO_CRM_NODE_ID = 'No en CRM';
const NO_CRM_NODE_COLOR = '#b0a99f';

const NODE_COLORS: Record<string, string> = {
  'Total de Leads': NAVY,
  Contactados: '#25616F',
  Fallidos: '#d32f2f',
  Descartados: '#9e9e9e',
  Pendientes: YELLOW,
  Respondieron: '#2C5889',
  'No Respondieron': '#7a8ba8',
  'En CRM': '#25616F',
  'Sin Clasificar': '#b0a99f',
};

// Maps each clickable node id to the extra filter params used when navigating to /leads.
// Nodes not present here are non-interactive (e.g. 'Sin Clasificar').
const NODE_FILTERS: Record<string, Record<string, boolean | string>> = {
  'Total de Leads': {},
  Contactados: { leadsContacted: true },
  Fallidos: { leadsFailed: true },
  Descartados: { discarded: true },
  Pendientes: { pendingLeads: true },
  Respondieron: { leadsContacted: true, hasClientMessages: true },
  'No Respondieron': { leadsContacted: true, noClientMessages: true },
  'En CRM': { CRMUpdateAt: true },
};

// @mui/icons-material/ManageSearch path data — list with magnifying glass, signals "view filtered list".
const MANAGE_SEARCH_PATH =
  'M7 9H2v2h5V9zm0 4H2v2h5v-2zm13.59 7l-3.83-3.83c-.8.52-1.74.83-2.76.83-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5c0 1.02-.31 1.96-.83 2.75L22 18.59 20.59 20zM17 12c0-1.65-1.35-3-3-3s-3 1.35-3 3 1.35 3 3 3 3-1.35 3-3zM2 7h9V5H2v2z';

// YIQ brightness — used to pick a readable icon color against each node's fill.
const hexBrightness = (hex: string) => {
  const h = hex.replace('#', '');
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
};

const LeadsSankey: FC<LeadsSankeyProps> = ({
  totalLeads,
  leadsContacted,
  leadsFailed,
  leadsDiscarded,
  leadsPending,
  leadsResponded,
  leadsInCRM,
  respondedLeadsInCRM,
  notRespondedLeadsInCRM,
  failedLeadsInCRM,
  discardedLeadsInCRM,
  pendingLeadsInCRM,
  failedLeadsByReason,
  failedLeadsByReasonCRM,
  filters,
  onNavigateToLeads,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showFailBreakdown, setShowFailBreakdown] = useState(false);

  // Reason buckets with > 0 leads. Keep `null` bucket as "Sin clasificar" so the
  // Fallidos out-flow conserves the same total (leadsFailed) as the no-breakdown view.
  const failReasonEntries = useMemo(() => {
    if (!failedLeadsByReason) return [] as { key: string; label: string; value: number; crm: number }[];
    return Object.entries(failedLeadsByReason)
      .filter(([, v]) => (v ?? 0) > 0)
      .map(([key, value]) => ({
        key,
        label: key === 'null' ? 'Sin clasificar' : (FAIL_REASON_LABELS[key as FailReason] ?? key),
        value: value as number,
        crm: failedLeadsByReasonCRM?.[key as FailReason | 'null'] ?? 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [failedLeadsByReason, failedLeadsByReasonCRM]);

  const hasFailBreakdown = failReasonEntries.length > 0;
  const breakdownActive = showFailBreakdown && hasFailBreakdown;

  const handleNodeClick = (nodeId: string) => {
    if (!filters || !onNavigateToLeads) return;
    // Fail-reason terminal nodes — `fail:<reason>` → filter Fallidos by that reason.
    // `null` bucket = unclassified, mapped to `failReason isEmpty` by `buildLeadsFilterUrl`.
    if (nodeId.startsWith(FAIL_NODE_PREFIX)) {
      const reason = nodeId.slice(FAIL_NODE_PREFIX.length);
      const url = buildLeadsFilterUrl({
        ...filters,
        leadsFailed: true,
        failReason: reason,
        viewMode: 'table',
      });
      onNavigateToLeads(url);
      return;
    }
    if (nodeId === NO_CRM_NODE_ID) {
      const url = buildLeadsFilterUrl({
        ...filters,
        leadsFailed: true,
        noCRMUpdateAt: true,
        viewMode: 'table',
      });
      onNavigateToLeads(url);
      return;
    }
    const additionalFilters = NODE_FILTERS[nodeId];
    if (additionalFilters === undefined) return;
    const url = buildLeadsFilterUrl({
      ...filters,
      ...additionalFilters,
      viewMode: 'table',
    });
    onNavigateToLeads(url);
  };
  const handleLinkClick = (sourceId: string, targetId: string) => {
    if (!filters || !onNavigateToLeads) return;
    const sourceFilters = NODE_FILTERS[sourceId];
    const targetFilters = NODE_FILTERS[targetId];
    if (sourceFilters === undefined && targetFilters === undefined) return;
    const url = buildLeadsFilterUrl({
      ...filters,
      ...(sourceFilters ?? {}),
      ...(targetFilters ?? {}),
      viewMode: 'table',
    });
    onNavigateToLeads(url);
  };
  const isClickable = (nodeId: string) => !!filters && !!onNavigateToLeads && Object.hasOwn(NODE_FILTERS, nodeId);

  // Derived values
  const leadsNotResponded = Math.max(leadsContacted - leadsResponded, 0);
  const classifiedLeads = leadsContacted + leadsFailed + leadsDiscarded + leadsPending;
  const unclassifiedLeads = Math.max(totalLeads - classifiedLeads, 0);

  // Build nodes — order matters (sort="input"): Contactados/En CRM on top
  // Terminal nodes (Pendientes, etc.) get pushed to the last column by align="justify",
  // so En CRM must come before them in the array to stay on top.
  // While breakdownActive the Sankey collapses to the failed-motive sub-flow only:
  //   Total de Leads → Fallidos → fail:reason → En CRM
  // so every other branch is dropped from the graph.
  const allNodes = breakdownActive
    ? [
        { id: 'Total de Leads' },
        { id: 'Fallidos' },
        { id: 'En CRM' },
        { id: NO_CRM_NODE_ID },
        ...failReasonEntries.map((e) => ({ id: `${FAIL_NODE_PREFIX}${e.key}` })),
      ]
    : [
        { id: 'Total de Leads' },
        { id: 'Contactados' },
        { id: 'Respondieron' },
        { id: 'En CRM' },
        { id: 'No Respondieron' },
        { id: 'Pendientes' },
        { id: 'Fallidos' },
        { id: 'Descartados' },
        { id: 'Sin Clasificar' },
      ];

  // Build links — order matters: Contactados first to keep it on top.
  // Each link carries `realValue` (true count, used for labels and tooltips) plus a
  // `value` floored to MIN_LINK_VALUE so sub-pixel ribbons stay clickable. The floor
  // intentionally distorts proportions for tiny buckets — the tradeoff for hit-area.
  const rawLinks = breakdownActive
    ? [
        { source: 'Total de Leads', target: 'Fallidos', realValue: leadsFailed },
        ...failReasonEntries.flatMap((e) => {
          const crm = Math.min(e.crm, e.value);
          const noCrm = e.value - crm;
          return [
            { source: 'Fallidos', target: `${FAIL_NODE_PREFIX}${e.key}`, realValue: e.value },
            { source: `${FAIL_NODE_PREFIX}${e.key}`, target: 'En CRM', realValue: crm },
            { source: `${FAIL_NODE_PREFIX}${e.key}`, target: NO_CRM_NODE_ID, realValue: noCrm },
          ];
        }),
      ]
    : [
        // Stage 1: Total → categories (Contactados first)
        { source: 'Total de Leads', target: 'Contactados', realValue: leadsContacted },
        { source: 'Total de Leads', target: 'Pendientes', realValue: leadsPending },
        { source: 'Total de Leads', target: 'Descartados', realValue: leadsDiscarded },
        { source: 'Total de Leads', target: 'Fallidos', realValue: leadsFailed },
        { source: 'Total de Leads', target: 'Sin Clasificar', realValue: unclassifiedLeads },

        // Stage 2: Contactados → response
        { source: 'Contactados', target: 'Respondieron', realValue: leadsResponded },
        { source: 'Contactados', target: 'No Respondieron', realValue: leadsNotResponded },

        // Stage 3: → CRM (En CRM links first to keep it on top)
        { source: 'Respondieron', target: 'En CRM', realValue: respondedLeadsInCRM },
        { source: 'No Respondieron', target: 'En CRM', realValue: notRespondedLeadsInCRM },
        { source: 'Pendientes', target: 'En CRM', realValue: pendingLeadsInCRM },
        { source: 'Fallidos', target: 'En CRM', realValue: failedLeadsInCRM },
        { source: 'Descartados', target: 'En CRM', realValue: discardedLeadsInCRM },
      ];

  // Floor used when rendering ribbons so tiny buckets stay clickable. 1% of the largest
  // visible total (Total de Leads in normal mode, Fallidos in breakdown mode), with a
  // hard minimum of 1 so non-empty buckets always render.
  const referenceTotal = breakdownActive ? leadsFailed : totalLeads;
  const MIN_LINK_VALUE = Math.max(1, Math.round(referenceTotal * 0.01));

  const validLinks = rawLinks
    .filter((l) => l.realValue > 0)
    .map((l) => ({ ...l, value: Math.max(l.realValue, MIN_LINK_VALUE) }));
  const connectedNodeIds = new Set(validLinks.flatMap((l) => [l.source, l.target]));

  // Compute node totals for labels
  const nodeValues: Record<string, number> = {};
  for (const link of validLinks) {
    nodeValues[link.source] = (nodeValues[link.source] || 0) + link.value;
  }
  // For terminal nodes (no outgoing links), sum incoming
  for (const link of validLinks) {
    if (!nodeValues[link.target]) {
      nodeValues[link.target] = 0;
    }
  }
  for (const link of validLinks) {
    const hasOutgoing = validLinks.some((l) => l.source === link.target);
    if (!hasOutgoing) {
      nodeValues[link.target] = (nodeValues[link.target] || 0) + link.value;
    }
  }
  // Fix: use known values for accuracy. Under breakdownActive the graph is reduced to
  // the failed sub-flow, so Total de Leads and En CRM are labelled with that subset.
  const knownValues: Record<string, number> = breakdownActive
    ? {
        'Total de Leads': leadsFailed,
        Fallidos: leadsFailed,
        'En CRM': failReasonEntries.reduce((acc, e) => acc + Math.min(e.crm, e.value), 0),
        [NO_CRM_NODE_ID]: failReasonEntries.reduce((acc, e) => acc + (e.value - Math.min(e.crm, e.value)), 0),
      }
    : {
        'Total de Leads': totalLeads,
        Contactados: leadsContacted,
        Respondieron: leadsResponded,
        'No Respondieron': leadsNotResponded,
        Fallidos: leadsFailed,
        Descartados: leadsDiscarded,
        Pendientes: leadsPending,
        'Sin Clasificar': unclassifiedLeads,
        'En CRM': leadsInCRM,
      };
  if (breakdownActive) {
    failReasonEntries.forEach((e) => {
      knownValues[`${FAIL_NODE_PREFIX}${e.key}`] = e.value;
    });
  }

  // Friendly label for fail-reason nodes — strip the prefix and use the Spanish label.
  const displayId = (id: string): string => {
    if (id.startsWith(FAIL_NODE_PREFIX)) {
      const key = id.slice(FAIL_NODE_PREFIX.length);
      return key === 'null' ? 'Sin clasificar' : (FAIL_REASON_LABELS[key as FailReason] ?? key);
    }
    return id;
  };

  const colorForId = (id: string): string => {
    if (id.startsWith(FAIL_NODE_PREFIX)) return FAIL_REASON_NODE_COLOR;
    if (id === NO_CRM_NODE_ID) return NO_CRM_NODE_COLOR;
    return NODE_COLORS[id] || '#15183A';
  };

  const validNodes = allNodes.filter((n) => connectedNodeIds.has(n.id)).map((n) => ({ ...n, color: colorForId(n.id) }));

  const hasData = totalLeads > 0 && validLinks.length > 0;

  if (!hasData) {
    return (
      <Box
        sx={{
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_COIN,
          boxShadow: SHADOW,
          p: { xs: 2.5, md: 4 },
          my: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: `${NAVY}73`,
            mb: 0.75,
          }}
        >
          Flujo
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            fontSize: { xs: '1.1rem', md: '1.25rem' },
            color: NAVY,
            mb: 1,
          }}
        >
          Cómo se mueven los leads de la creación al CRM
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 5, md: 7 },
            mt: 2,
            backgroundColor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_CARD,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: NAVY,
              mb: 0.5,
            }}
          >
            No hay leads en el período seleccionado
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.8rem',
              color: `${NAVY}80`,
              textAlign: 'center',
              maxWidth: 360,
            }}
          >
            Ajusta el rango de fechas o los filtros para ver el flujo de leads.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Compute dynamic margins based on label lengths
  const makeLabel = (id: string) => {
    const val = knownValues[id];
    const name = displayId(id);
    return val !== undefined ? `${name} (${val.toLocaleString()})` : name;
  };
  const terminalNodeIds = new Set(validNodes.map((n) => n.id).filter((id) => !validLinks.some((l) => l.source === id)));
  const leftNodeIds = validNodes.map((n) => n.id).filter((id) => !terminalNodeIds.has(id));
  const charWidth = 6.5;
  const labelPadding = 16;
  const iconSize = 18;
  const nodeThickness = 28;
  // Horizontal layout: labels render to the left of source/intermediate nodes and to the
  // right of terminals. Vertical (mobile) layout: each bar's label goes outside the chart
  // on the side closer to that bar (left or right) — sibling bars in the same row land on
  // opposite sides instead of colliding above/below. Margins must fit the widest full label.
  const horizontalLeftLabelWidth = Math.max(
    ...leftNodeIds.map((id) => makeLabel(id).length * charWidth + labelPadding),
    80
  );
  const horizontalRightLabelWidth =
    Math.max(...[...terminalNodeIds].map((id) => makeLabel(id).length * charWidth + labelPadding), 80) + 16;
  const verticalSideMargin = Math.max(...validNodes.map((n) => makeLabel(n.id).length * charWidth + labelPadding), 80);
  const leftMargin = isMobile ? verticalSideMargin : horizontalLeftLabelWidth;
  const rightMargin = isMobile ? verticalSideMargin : horizontalRightLabelWidth;
  const topMargin = isMobile ? 12 : 20;
  const bottomMargin = isMobile ? 12 : 20;

  // Custom labels layer: replaces nivo's default 'labels' layer so each label
  // (text + ManageSearch icon) becomes a clickable affordance for opening the
  // filtered leads view — not just the node rect.
  const CustomLabels = ({
    nodes,
  }: {
    nodes: readonly { id: string | number; x0: number; x1: number; y0: number; y1: number }[];
  }) => {
    if (nodes.length === 0) return null;
    const maxX1 = Math.max(...nodes.map((n) => n.x1));
    const minNodeX = Math.min(...nodes.map((n) => n.x0));
    const maxNodeX = Math.max(...nodes.map((n) => n.x1));
    const chartCenterX = (minNodeX + maxNodeX) / 2;
    return (
      <g>
        {nodes.map((node) => {
          const id = String(node.id);
          const text = makeLabel(id);
          const color = NODE_COLORS[id] || NAVY;
          const xCenter = (node.x0 + node.x1) / 2;
          const yCenter = (node.y0 + node.y1) / 2;
          const clickable = isClickable(id);
          const badgePadding = 4;
          const badgeSize = iconSize + badgePadding * 2;

          // Layout-aware positioning. Horizontal: bar is vertical (thin x-span, tall y-span),
          // labels sit left/right outside, icon anchors to the bottom (y end) of the bar.
          // Vertical (mobile): bar is horizontal (wide x-span, short y-span), labels sit
          // above/below the bar (text stays horizontal so it remains readable), icon anchors
          // to the right (x end) of the bar — same "al final" semantics along the flow axis.
          let textX: number;
          let textY: number;
          let textAnchor: 'start' | 'middle' | 'end';
          let dominantBaseline: 'central' | 'auto' | 'hanging';
          let iconX: number;
          let iconY: number;
          let needsBacking: boolean;

          if (isMobile) {
            // Side-of-bar placement: pick whichever side the bar is closer to, so sibling
            // bars in the same row land on opposite sides instead of stacking on top of
            // each other above/below the bar.
            const useRightSide = xCenter > chartCenterX;
            textY = yCenter;
            dominantBaseline = 'central';
            if (useRightSide) {
              textAnchor = 'start';
              textX = node.x1 + labelPadding;
            } else {
              textAnchor = 'end';
              textX = node.x0 - labelPadding;
            }
            const barWidth = node.x1 - node.x0;
            const fitsAtEnd = barWidth >= badgeSize;
            iconY = yCenter - iconSize / 2;
            iconX = fitsAtEnd ? node.x1 - iconSize - badgePadding : xCenter - iconSize / 2;
            needsBacking = !fitsAtEnd;
          } else {
            const isRightmost = node.x1 === maxX1;
            textY = yCenter;
            dominantBaseline = 'central';
            if (isRightmost) {
              textAnchor = 'start';
              textX = node.x1 + labelPadding;
            } else {
              textAnchor = 'end';
              textX = node.x0 - labelPadding;
            }
            const barHeight = node.y1 - node.y0;
            const fitsAtEnd = barHeight >= badgeSize;
            iconX = xCenter - iconSize / 2;
            iconY = fitsAtEnd ? node.y1 - iconSize - badgePadding : yCenter - iconSize / 2;
            needsBacking = !fitsAtEnd;
          }
          const backingFill = color;

          const labelText = (
            <text
              x={textX}
              y={textY}
              textAnchor={textAnchor}
              dominantBaseline={dominantBaseline}
              style={{
                fontFamily: 'Jost, sans-serif',
                fontSize: 13,
                fontWeight: 600,
                fill: color,
              }}
            >
              {text}
            </text>
          );

          if (!clickable) {
            return <g key={id}>{labelText}</g>;
          }

          return (
            // biome-ignore lint/a11y/useSemanticElements: <button> cannot be a direct child of SVG; role="button" on <g> is the SVG-compatible equivalent.
            <g
              key={id}
              style={{ cursor: 'pointer' }}
              onClick={() => handleNodeClick(id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNodeClick(id);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Ver leads de ${id}`}
            >
              {labelText}
              {needsBacking && (
                <rect
                  x={iconX - badgePadding}
                  y={iconY - badgePadding}
                  width={badgeSize}
                  height={badgeSize}
                  rx={6}
                  ry={6}
                  fill={backingFill}
                />
              )}
              <svg
                x={iconX}
                y={iconY}
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path d={MANAGE_SEARCH_PATH} fill={hexBrightness(color) > 140 ? NAVY : '#FFFFFF'} />
              </svg>
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_COIN,
        boxShadow: SHADOW,
        p: { xs: 2.5, md: 4 },
        my: 2,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0px 16px 40px rgba(21, 24, 58, 0.10)' },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 600,
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: `${NAVY}73`,
          mb: 0.75,
        }}
      >
        Flujo
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 700,
          fontSize: { xs: '1.1rem', md: '1.25rem' },
          color: NAVY,
          mb: 0.5,
        }}
      >
        Cómo se mueven los leads de la creación al CRM
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.875rem',
          color: `${NAVY}80`,
          mb: 2.5,
        }}
      >
        Haz clic en cualquier nodo para abrir la vista de leads filtrada por ese estado.
      </Typography>

      {isMobile ? (
        <MobileFlow
          totalLeads={totalLeads}
          knownValues={knownValues}
          onRowClick={handleNodeClick}
          canClick={isClickable}
        />
      ) : (
        <>
          {hasFailBreakdown && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showFailBreakdown}
                    onChange={(_, checked) => setShowFailBreakdown(checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#d32f2f' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#d32f2f' },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#15183A' }}
                  >
                    Desglosar motivos de fallo
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          )}
          <Box sx={{ height: { xs: 520, md: 420 }, '& rect, & path': { cursor: 'pointer' } }}>
            <ResponsiveSankey
              data={{ nodes: validNodes, links: validLinks }}
              sort={breakdownActive ? 'auto' : 'input'}
              layout="horizontal"
              margin={{ top: topMargin, right: rightMargin, bottom: bottomMargin, left: leftMargin }}
              align="justify"
              colors={(node) => colorForId(node.id as string)}
              nodeOpacity={1}
              nodeHoverOpacity={1}
              nodeHoverOthersOpacity={0.25}
              onClick={(data) => {
                if ('id' in data) {
                  handleNodeClick(data.id as string);
                } else if ('source' in data && 'target' in data) {
                  const link = data as { source: { id: string }; target: { id: string } };
                  handleLinkClick(link.source.id, link.target.id);
                }
              }}
              nodeThickness={nodeThickness}
              nodeSpacing={28}
              nodeBorderWidth={0}
              nodeBorderRadius={6}
              linkOpacity={0.2}
              linkHoverOpacity={0.5}
              linkHoverOthersOpacity={0.08}
              linkContract={3}
              linkBlendMode="normal"
              enableLinkGradient
              layers={['links', 'nodes', CustomLabels]}
              theme={{
                text: {
                  fontFamily: 'Jost, sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                },
                tooltip: {
                  container: {
                    fontFamily: 'Jost, sans-serif',
                    fontSize: 13,
                    borderRadius: 16,
                    boxShadow: SHADOW,
                    border: 'none',
                    padding: '10px 14px',
                  },
                },
              }}
              nodeTooltip={({ node }) => {
                const dropoffSteps: Record<string, { previous: number; from: string }> = {
                  Contactados: { previous: totalLeads, from: 'Total de Leads' },
                  Respondieron: { previous: leadsContacted, from: 'Contactados' },
                };
                const dropoff = dropoffSteps[node.id as string];
                // Use the real (un-floored) value so tooltips stay truthful even when the
                // ribbon has been padded to the click-area floor.
                const nodeValue = knownValues[node.id as string] ?? (node.value as number);
                const pctBase = dropoff ? dropoff.previous : totalLeads;
                const pctLabel = dropoff ? dropoff.from : 'total';
                const pct = pctBase > 0 ? ((nodeValue / pctBase) * 100).toFixed(1) : '0';
                const lost = dropoff ? dropoff.previous - nodeValue : 0;
                const lostPct = dropoff && dropoff.previous > 0 ? ((lost / dropoff.previous) * 100).toFixed(1) : null;
                return (
                  <Box
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      backgroundColor: SURFACE_CONTAINER_LOWEST,
                      borderRadius: RADIUS_CARD,
                      boxShadow: SHADOW,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#15183A' }}
                    >
                      {displayId(node.id as string)}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', color: 'rgba(21, 24, 58, 0.6)' }}
                    >
                      {nodeValue.toLocaleString()} leads ({pct}% de {pctLabel})
                    </Typography>
                    {lostPct && lost > 0 && dropoff && (
                      <Typography
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '0.75rem',
                          color: '#d32f2f',
                          fontWeight: 600,
                          mt: 0.5,
                        }}
                      >
                        {lost.toLocaleString()} leads ({lostPct}%) no pasaron de {dropoff.from}
                      </Typography>
                    )}
                  </Box>
                );
              }}
              linkTooltip={({ link }) => {
                // Use real values (not floored render values) so tiny buckets show truthful counts.
                const linkValue = ((link as { realValue?: number }).realValue ?? link.value) as number;
                const sourceId = link.source.id as string;
                const targetId = link.target.id as string;
                const sourceValue = knownValues[sourceId] ?? (link.source.value as number);
                const linkPct = sourceValue > 0 ? ((linkValue / sourceValue) * 100).toFixed(1) : '0';
                const lost = sourceValue - linkValue;
                const lostPct = sourceValue > 0 ? ((lost / sourceValue) * 100).toFixed(1) : null;
                const hideDropoff = new Set([
                  'Total de Leads→Pendientes',
                  'Total de Leads→Descartados',
                  'Total de Leads→Fallidos',
                  'Total de Leads→Sin Clasificar',
                  'Contactados→No Respondieron',
                ]);
                const linkKey = `${sourceId}→${targetId}`;
                // Per-reason breakdown is an exhaustive split of Fallidos — drop-off is always 0/misleading.
                // Same for each reason → (En CRM | No en CRM) that follows.
                const isFailBreakdownLink =
                  (sourceId === 'Fallidos' && targetId.startsWith(FAIL_NODE_PREFIX)) ||
                  (sourceId.startsWith(FAIL_NODE_PREFIX) && (targetId === 'En CRM' || targetId === NO_CRM_NODE_ID));
                const showDropoff = !isFailBreakdownLink && !hideDropoff.has(linkKey) && lostPct && lost > 0;
                return (
                  <Box
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      backgroundColor: SURFACE_CONTAINER_LOWEST,
                      borderRadius: RADIUS_CARD,
                      boxShadow: SHADOW,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#15183A' }}
                    >
                      {displayId(sourceId)} → {displayId(targetId)}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', color: 'rgba(21, 24, 58, 0.6)' }}
                    >
                      {linkValue.toLocaleString()} leads ({linkPct}% de {displayId(sourceId)})
                    </Typography>
                    {showDropoff && (
                      <Typography
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '0.75rem',
                          color: '#d32f2f',
                          fontWeight: 600,
                          mt: 0.5,
                        }}
                      >
                        {lost.toLocaleString()} leads ({lostPct}%) de {displayId(sourceId)} no llegaron a{' '}
                        {displayId(targetId)}
                      </Typography>
                    )}
                  </Box>
                );
              }}
            />
          </Box>
        </>
      )}

      {/* Collapsible status descriptions */}
      <StatusDescriptions visibleNodeIds={connectedNodeIds} />
    </Box>
  );
};

// Mobile-only alternative view: stage-grouped stacked bars. The Sankey is too cramped on
// narrow screens (tiny bars, colliding labels). This shows each stage as its own card with
// a labeled, proportionally-sized clickable row per state — same data, no layout collisions.
const MOBILE_STAGES: { title: string; parentId: 'Total de Leads' | 'Contactados' | null; rows: string[] }[] = [
  { title: 'Total', parentId: null, rows: ['Total de Leads'] },
  {
    title: 'Categoría inicial',
    parentId: 'Total de Leads',
    rows: ['Contactados', 'Pendientes', 'Descartados', 'Fallidos', 'Sin Clasificar'],
  },
  { title: 'Respuesta de contactados', parentId: 'Contactados', rows: ['Respondieron', 'No Respondieron'] },
  { title: 'En CRM', parentId: 'Total de Leads', rows: ['En CRM'] },
];

const MobileFlow: FC<{
  totalLeads: number;
  knownValues: Record<string, number>;
  onRowClick: (nodeId: string) => void;
  canClick: (nodeId: string) => boolean;
}> = ({ totalLeads, knownValues, onRowClick, canClick }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {MOBILE_STAGES.map((stage) => {
        const visibleRows = stage.rows.filter((id) => (knownValues[id] ?? 0) > 0);
        if (visibleRows.length === 0) return null;
        const parentValue = stage.parentId === null ? totalLeads : (knownValues[stage.parentId] ?? totalLeads);
        return (
          <Box
            key={stage.title}
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOW,
              borderRadius: RADIUS_CARD,
              p: 1.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: `${NAVY}73`,
                mb: 1,
              }}
            >
              {stage.title}
              {stage.parentId !== null && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    color: `${NAVY}55`,
                    textTransform: 'none',
                    letterSpacing: 0,
                    ml: 0.5,
                  }}
                >
                  · de {stage.parentId}
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {visibleRows.map((id) => {
                const value = knownValues[id] ?? 0;
                const pct = parentValue > 0 ? (value / parentValue) * 100 : 0;
                const color = NODE_COLORS[id] || NAVY;
                const clickable = canClick(id);
                const handle = clickable ? () => onRowClick(id) : undefined;
                return (
                  <Box
                    key={id}
                    onClick={handle}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick(id);
                            }
                          }
                        : undefined
                    }
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    aria-label={clickable ? `Ver leads de ${id}` : undefined}
                    sx={{
                      cursor: clickable ? 'pointer' : 'default',
                      backgroundColor: SURFACE_CONTAINER_LOWEST,
                      borderRadius: RADIUS_CARD,
                      px: 1.25,
                      py: 1,
                      transition: 'background-color 0.15s ease',
                      '&:hover': clickable ? { backgroundColor: `${color}14` } : undefined,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: NAVY,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {id}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          color: NAVY,
                          flexShrink: 0,
                        }}
                      >
                        {value.toLocaleString()}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '0.75rem',
                          color: `${NAVY}80`,
                          flexShrink: 0,
                        }}
                      >
                        · {pct < 0.1 && pct > 0 ? '<0.1' : pct.toFixed(1)}%
                      </Typography>
                      {clickable && (
                        <Box
                          component="svg"
                          viewBox="0 0 24 24"
                          sx={{ width: 16, height: 16, flexShrink: 0, color: `${NAVY}80` }}
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path d={MANAGE_SEARCH_PATH} fill="currentColor" />
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        height: 6,
                        borderRadius: 999,
                        backgroundColor: `${color}1f`,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          width: `${Math.max(Math.min(pct, 100), value > 0 ? 2 : 0)}%`,
                          backgroundColor: color,
                          borderRadius: 999,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const STATUS_DESCRIPTIONS: { id: string; label: string; description: string; color: string }[] = [
  {
    id: 'Total de Leads',
    label: 'Total de Leads',
    description: 'Todos los leads del período.',
    color: '#15183A',
  },
  {
    id: 'Contactados',
    label: 'Contactados',
    description: 'Se les envió al menos un mensaje exitosamente.',
    color: '#25616F',
  },
  {
    id: 'Pendientes',
    label: 'Pendientes',
    description: 'De campañas outbound, aún no se les ha enviado mensaje.',
    color: '#F9D00A',
  },
  {
    id: 'Descartados',
    label: 'Descartados',
    description: 'No se contactaron por ser duplicados o contactados recientemente.',
    color: '#9e9e9e',
  },
  {
    id: 'Fallidos',
    label: 'Fallidos',
    description: 'El envío falló, probablemente por teléfono incorrecto.',
    color: '#d32f2f',
  },
  {
    id: 'Respondieron',
    label: 'Respondieron',
    description: 'El cliente envió al menos un mensaje de vuelta.',
    color: '#2C5889',
  },
  {
    id: 'No Respondieron',
    label: 'No Respondieron',
    description: 'Recibieron el mensaje pero no respondieron.',
    color: '#7a8ba8',
  },
  {
    id: 'En CRM',
    label: 'En CRM',
    description: 'Subidos al CRM, desde cualquier categoría.',
    color: '#25616F',
  },
  {
    id: 'Sin Clasificar',
    label: 'Sin Clasificar',
    description: 'No encajan en ninguna categoría, posible caso borde.',
    color: '#b0a99f',
  },
  {
    id: 'No en CRM',
    label: 'No en CRM',
    description: 'Leads fallidos que no llegaron al CRM.',
    color: '#b0a99f',
  },
];

const StatusDescriptions: FC<{ visibleNodeIds: Set<string> }> = ({ visibleNodeIds }) => {
  const [open, setOpen] = useState(false);
  const visibleDescriptions = STATUS_DESCRIPTIONS.filter((s) => visibleNodeIds.has(s.id));

  if (visibleDescriptions.length === 0) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { opacity: 0.7 },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 14, color: 'rgba(21, 24, 58, 0.4)' }} />
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'rgba(21, 24, 58, 0.4)',
          }}
        >
          {open ? 'Ocultar' : 'Ver estados'}
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, rowGap: 0.25 }}>
          {visibleDescriptions.map((status) => (
            <Box key={status.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: status.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.75rem',
                  color: '#15183A',
                  lineHeight: 1.3,
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 700 }}
                >
                  {status.label}:
                </Typography>{' '}
                {status.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default LeadsSankey;
